import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useWidgetState } from '@livechat/widget-react';
import { RootState, RootDispatch } from 'storage/store';
import {
  Box,
  Container,
  Link,
  Typography,
  useMediaQuery,
  Grid
} from '@mui/material';
import { NavLink } from 'react-router';
import theme from 'App.theme';
import { LiveChatAgentStatus } from 'utils/consts';
import { Icon } from 'components/Icon';
import { footerClickEvent } from 'utils/events';
import { IsLoggedIn } from 'utils/user';
import sendEvent from 'utils/sendEvent';
import { Url } from 'utils/urls';
import ContactWrapper from './styled/ContactWrapper';
import FooterWrapper from './styled/FooterWrapper';
import pjson from '../../../package.json';

const Footer = () => {
  const { modals } = useDispatch<RootDispatch>();
  const { t } = useTranslation();
  const widgetState = useWidgetState();
  const phoneNumber = t('COMMON_PHONE_NUMBER');
  const { application } = useDispatch<RootDispatch>();
  const applicationVersion = useSelector(
    (state: RootState) => state.application
  );
  const { bffVersion, adapterVersion } = applicationVersion;
  const fedVersion = pjson?.version;
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const widgetIsAvailable =
    IsLoggedIn() && widgetState?.availability === LiveChatAgentStatus.online;

  useEffect(() => {
    loadApplicationVersion();
  }, []);

  const loadApplicationVersion = async () => {
    await application.getApplicationVersion();
  };

  const handleClick = () => {
    modals.changeRegulationsVisibility();
    sendEvent({
      labelText: t('COMMON_REGULATIONS'),
      ...footerClickEvent
    });
  };

  return (
    <footer>
      <FooterWrapper
        sx={
          widgetIsAvailable
            ? {
                [theme.breakpoints.down('md')]: {
                  minHeight: '146px'
                },
                [theme.breakpoints.down('lg')]: {
                  minHeight: '126px'
                }
              }
            : null
        }
      >
        <Container maxWidth="xl">
          <Grid container>
            <Grid
              size={{ xs: 12, lg: 6 }}
              sx={{
                minHeight: '100%',
                alignItems: 'center'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  [theme.breakpoints.up('md')]: {
                    marginLeft: '-22px'
                  }
                }}
              >
                <Icon
                  icon="phone"
                  sx={{
                    marginRight: '21px',
                    marginTop: '6px',
                    color: 'primary.light'
                  }}
                  fontSize="medium"
                />
                <ContactWrapper>
                  <Typography
                    variant="body1"
                    sx={{
                      [theme.breakpoints.down('md')]: {
                        marginBottom: '4px'
                      }
                    }}
                  >
                    {t('FOOTER_CONTACT_NAME')}&nbsp;
                    <Link
                      href={`tel:${phoneNumber.replace(/ /g, '')}`}
                      onClick={() =>
                        sendEvent({
                          labelText: phoneNumber,
                          ...footerClickEvent
                        })
                      }
                      data-link-no-alert
                    >
                      {phoneNumber}
                    </Link>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      [theme.breakpoints.down('md')]: {
                        flexFlow: 'column'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 400 }}>
                      {t(
                        mobileBreakpoint
                          ? 'FOOTER_CONTACT_WORKWEEKHOURS_MOBILE'
                          : 'FOOTER_CONTACT_WORKWEEKHOURS'
                      )}
                      &nbsp;
                    </Typography>
                  </Box>
                </ContactWrapper>
              </Box>
              {widgetIsAvailable && (
                <Box
                  sx={{
                    display: 'flex',
                    marginTop: '15px',
                    [theme.breakpoints.up('md')]: {
                      marginLeft: '-22px'
                    }
                  }}
                >
                  <Icon icon="chat" color="success" />
                  <Grid
                    size={11}
                    sx={{
                      paddingBottom: '6px',
                      marginLeft: '21px',
                      marginTop: '-3px'
                    }}
                  >
                    <Link
                      onClick={() =>
                        (window as any)?.LC_API?.open_chat_window()
                      }
                      sx={{
                        color: 'primary.light',
                        fontWeight: 700,
                        lineHeight: '125%',
                        cursor: 'pointer'
                      }}
                    >
                      {t('CHAT_CONVERSATION')}
                    </Link>
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid
              size={{ lg: 6 }}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-evenly',
                marginLeft: '-40px'
              }}
            >
              <Link
                variant="subtitle1"
                to={Url.privacyPolicy}
                component={NavLink}
                onClick={() =>
                  sendEvent({
                    labelText: t('COMMON_PRIVACY_POLICY'),
                    ...footerClickEvent
                  })
                }
              >
                {t('COMMON_PRIVACY_POLICY')}
              </Link>
              <Link
                variant="subtitle1"
                component="button"
                onClick={handleClick}
              >
                {t('COMMON_REGULATIONS')}
              </Link>
              <Link
                variant="subtitle1"
                to={Url.faq}
                component={NavLink}
                onClick={() => {
                  sendEvent({
                    labelText: t('FOOTER_FAQ_PAGE'),
                    ...footerClickEvent
                  });
                }}
              >
                {t('FOOTER_FAQ_PAGE')}
              </Link>
            </Grid>
          </Grid>
          <Grid
            sx={{
              marginLeft: '-22px',
              [theme.breakpoints.down('lg')]: {
                display: 'none'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                margin: '5px 0px 10px',
                color: 'common.white'
              }}
            >
              front: {fedVersion} | bff: {bffVersion} | adapter:{' '}
              {adapterVersion}
            </Typography>
          </Grid>
        </Container>
      </FooterWrapper>
    </footer>
  );
};
export default Footer;
